// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHeaderForecast } from '../../hooks/useHeaderForecast';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useAnchoredPosition } from '../../hooks/useAnchoredPosition';
import type { WeatherCondition } from '../../types/weather';
import { useSiteTime } from '../../hooks/useSiteTime';
import type { SiteTimeValue } from '../../contexts/SiteTimeContext';

const SunIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--st-warn)"
    strokeWidth="1.8"
  >
    <circle cx="12" cy="12" r="4.5" />
    <path
      d="M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"
      strokeLinecap="round"
    />
  </svg>
);

const CloudIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--text-helper)"
    strokeWidth="1.8"
  >
    <path d="M7 18a4 4 0 0 1-.5-7.97A5 5 0 0 1 16.5 9 4.5 4.5 0 0 1 16 18H7z" />
  </svg>
);

const RainIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--link)"
    strokeWidth="1.8"
  >
    <path d="M7 16a4 4 0 0 1-.5-7.97A5 5 0 0 1 16.5 7 4.5 4.5 0 0 1 16 16H7z" />
    <path d="M8 19l-1 2M12 19l-1 2M16 19l-1 2" strokeLinecap="round" />
  </svg>
);

function ConditionIcon({ condition }: { condition: WeatherCondition }) {
  if (condition === 'rain') return <RainIcon />;
  if (condition === 'cloud') return <CloudIcon />;
  return <SunIcon />;
}

const HeaderSun = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
  >
    <circle cx="12" cy="12" r="4.5" />
    <path
      d="M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * iOS-style floating-bar geometry. Each bar spans the day's own [min, max]
 * mapped onto the whole week's [weekMin, weekMax] range — so its LENGTH is the
 * day's temperature spread and its POSITION shows where the day sits in the
 * week (left = cooler, right = warmer).
 *
 * The st-ok → st-warn gradient is painted across the FULL track and revealed
 * only within the bar: the gradient is stretched to track width via
 * background-size and shifted via background-position so the bar exposes the
 * slice that matches its place in the week. A cool day therefore shows only
 * green, a hot day amber, and a wide day the transition between them.
 */
export const MIN_WIDTH_PCT = 6;
export function bar(day: { min: number; max: number }, weekMin: number, weekMax: number) {
  const span = weekMax - weekMin || 1;
  const left = ((day.min - weekMin) / span) * 100;
  const rawWidth = ((day.max - day.min) / span) * 100;
  const width = Math.min(Math.max(rawWidth, MIN_WIDTH_PCT), 100 - left);
  // Stretch the gradient to the full track and offset it to this bar's slot.
  const backgroundSize = `${(100 / width) * 100}% 100%`;
  const backgroundPositionX = width >= 100 ? '0%' : `${(100 * left) / (100 - width)}%`;
  return { left, width, backgroundSize, backgroundPositionX };
}

/** "Today" for the first row, else the short weekday name ("Thu"). */
function dayLabel(date: Date, index: number, format: SiteTimeValue['format']): string {
  if (index === 0) return 'Today';
  return format(date, 'weekdayShort');
}

export default function WeatherWidget() {
  const { format } = useSiteTime();
  const { siteId } = useActiveSite();
  const [open, setOpen] = useState(false);
  const { forecast, status, refetch } = useHeaderForecast({ siteId });
  const ref = useRef<HTMLDivElement>(null);
  // The popover renders through a portal (see the `open &&` block below), so
  // it's no longer a DOM descendant of `ref` — outside-click detection has to
  // check both.
  const popoverRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredPosition(ref, open);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Clicking the widget always toggles the popover; when it's in the error
  // state a click also forces a fresh fetch cycle.
  const handleToggle = () => {
    if (status === 'error') refetch();
    setOpen(o => !o);
  };

  // The button is ALWAYS rendered. The temperature shows once loaded; before
  // that a muted em dash (no unit) keeps the button stable without the clipped
  // "—°F" placeholder.
  const ready = status === 'ready' && forecast?.current != null;
  const tempLabel = ready ? `${forecast!.current}${forecast!.unitSuffix}` : '—';

  const weekMin = forecast ? Math.min(...forecast.days.map(d => d.min)) : 0;
  const weekMax = forecast ? Math.max(...forecast.days.map(d => d.max)) : 0;
  const unit = forecast ? forecast.unitSuffix.replace(/[FC]$/, '') : '°';

  return (
    <div ref={ref} className="helios-weather" style={{ position: 'relative' }}>
      <button
        type="button"
        className="helios-weather-btn"
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={status === 'error' ? 'Weather unavailable — click to retry' : 'Weather forecast'}
      >
        <HeaderSun />
        <span
          className={`helios-weather-temp${ready ? '' : ' helios-weather-temp--muted'}`}
          data-testid="header-weather"
        >
          {tempLabel}
        </span>
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            className="helios-weather-popover"
            style={{ top: position.top, left: position.left }}
            role="dialog"
            aria-label="Weather forecast"
          >
            {status === 'loading' && <div className="helios-weather-msg">Loading forecast…</div>}
            {status === 'error' && (
              <div className="helios-weather-msg">
                Weather is unavailable right now.
                <button type="button" className="helios-weather-retry" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            )}
            {status === 'ready' &&
              forecast!.days.map((day, index) => {
                const b = bar(day, weekMin, weekMax);
                return (
                  <div key={day.date.toISOString()} className="helios-weather-row">
                    <span className="helios-weather-day">{dayLabel(day.date, index, format)}</span>
                    <ConditionIcon condition={day.condition} />
                    <span className="helios-weather-min">
                      {day.min}
                      {unit}
                    </span>
                    <div className="helios-weather-gauge">
                      <div
                        className="helios-weather-gauge-fill"
                        style={{
                          left: `${b.left}%`,
                          width: `${b.width}%`,
                          backgroundSize: b.backgroundSize,
                          backgroundPositionX: b.backgroundPositionX,
                        }}
                      />
                    </div>
                    <span className="helios-weather-max">
                      {day.max}
                      {unit}
                    </span>
                  </div>
                );
              })}
          </div>,
          document.body
        )}
    </div>
  );
}
